import { isWorkspaceArtifactNavigation } from '~/api/data/workspaces/revalidation';
import * as apm from '@oysterjs/core/apm';
import { LoaderFunction, matchPath, type ShouldRevalidateFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { loadQuery } from '~/api/data/responses';
import {
  AgencyUserFieldsFragment,
  GetAgencyUserDocument,
  GetAgencyUserQuery,
  GetAgencyUserQueryVariables,
  GetAgencyUsersDocument,
  GetAgencyUsersQuery,
  GetAgencyUsersQueryVariables,
  GetMyTeamsDocument,
  GetMyTeamsQuery,
  GetMyTeamsQueryVariables,
  GetUserProducerInformationsDocument,
  GetUserProducerInformationsQuery,
  GetUserProducerInformationsQueryVariables,
  LoggedInAgencyUserFieldsFragment,
  ProducerInformationFieldsFragment,
  TeamSummaryFieldsFragment
} from '~/api/graphql';

export const getAgencyUser = async () => {
  return queryData<
    GetAgencyUserQuery,
    GetAgencyUserQueryVariables,
    LoggedInAgencyUserFieldsFragment
  >(client, { query: GetAgencyUserDocument }, (data) => data?.agencyUser);
};

export const getAgencyUsers = async () => {
  return queryData<GetAgencyUsersQuery, GetAgencyUsersQueryVariables, AgencyUserFieldsFragment[]>(
    client,
    { query: GetAgencyUsersDocument },
    (data) => data?.agencyUsers || []
  );
};

export const getUserProducerInformations = async () =>
  queryData<
    GetUserProducerInformationsQuery,
    GetUserProducerInformationsQueryVariables,
    ProducerInformationFieldsFragment[]
  >(
    client,
    { query: GetUserProducerInformationsDocument, fetchPolicy: 'network-only' },
    (data) => data?.agencyUser?.producerInformations || []
  );

// Non-Enterprise users may lack team access. Keep the profile usable by returning
// an empty list on failure; the team section hides itself when the list is empty.
export const getMyTeams = async () => {
  try {
    const result = await client.query<GetMyTeamsQuery, GetMyTeamsQueryVariables>({
      query: GetMyTeamsDocument,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all'
    });
    return { data: result.data?.agencyUser?.teams ?? [] };
  } catch {
    return { data: [] as TeamSummaryFieldsFragment[] };
  }
};

export const loadAgencyUsers: LoaderFunction = () =>
  loadQuery(getAgencyUsers(), { notFoundRedirect: '/app/signin', suppressAuthErrors: true });

export const loadUserProducerInformations: LoaderFunction = () =>
  loadQuery(getUserProducerInformations(), {
    notFoundRedirect: '/app/signin',
    suppressAuthErrors: true
  });

export const loadMyTeams: LoaderFunction = () => loadQuery(getMyTeams());

// A colleague can grant or revoke mailbox access while this tab is elsewhere.
// The root context must refresh along with the workspace loader's user snapshot.
export const shouldRevalidateCurrentUser: ShouldRevalidateFunction = (args) =>
  !isWorkspaceArtifactNavigation(args) &&
  (args.defaultShouldRevalidate ||
    (args.currentUrl.pathname !== args.nextUrl.pathname &&
      !!matchPath('/app/workspaces/:id', args.nextUrl.pathname)));

export const loadCurrentUser = async () => {
  const user = await loadQuery(getAgencyUser(), { notFoundRedirect: '/app/signin' });
  if (user && !(user instanceof Response))
    apm.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
      agencyId: user.agency.id,
      agencyName: user.agency.name
    });
  return user;
};
